import { createUseStoreWithSelectorDeferred } from "@captions-web/utils/zustand-store";
import { z } from "zod";
import { createStore } from "zustand";
import { combine } from "zustand/middleware";

import { createLocalStorageDef } from "~/lib/local-storage";

import { createStoreGetters } from "./root";

export function createLayoutStore() {
  return createStore(
    combine(
      { sidebarCollapsed: shouldAutoCollapse() || getUserManuallyCollapsedSidebar() },
      (set, get) => {
        return {
          async init() {
            let prev = getCurrentSidebarBreakpoint();
            window.addEventListener("resize", () => {
              const cur = getCurrentSidebarBreakpoint();
              if (prev === "wide" && cur === "narrow") {
                // always auto collapse
                set({ sidebarCollapsed: true });
              } else if (prev === "narrow" && cur === "wide") {
                // auto expand unless user has manually changed it
                set({ sidebarCollapsed: getUserManuallyCollapsedSidebar() });
              }
              prev = cur;
            });
          },
          toggleSidebar() {
            const newVal = !get().sidebarCollapsed;
            if (getCurrentSidebarBreakpoint() === "wide") {
              setUserManuallyCollapsedSidebar(newVal);
            }
            set({ sidebarCollapsed: newVal });
          },
        };
      }
    )
  );
}

type LayoutStore = ReturnType<ReturnType<typeof createLayoutStore>["getState"]>;

export const {
  getStore: getLayoutStore,
  getStoreAsync: getLayoutStoreAsync,
  getStoreApi: getLayoutStoreApi,
} = createStoreGetters<LayoutStore>("layout");

export const useLayoutStore = createUseStoreWithSelectorDeferred(() => getLayoutStoreApi());

const AUTO_COLLAPSE_BREAKPOINT = 768;

function getCurrentSidebarBreakpoint() {
  if (window.innerWidth < AUTO_COLLAPSE_BREAKPOINT) {
    return "narrow" as const;
  } else {
    return "wide" as const;
  }
}

function shouldAutoCollapse() {
  return getCurrentSidebarBreakpoint() === "narrow";
}

const sidebarManuallyCollapsed = createLocalStorageDef("sidebarManuallyCollapsed", z.boolean());

function getUserManuallyCollapsedSidebar() {
  return sidebarManuallyCollapsed.get() ?? false;
}

function setUserManuallyCollapsedSidebar(collapsed: boolean) {
  sidebarManuallyCollapsed.set(collapsed);
}
